import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { ConvertRequest, ErpGroupOption } from "../../api/leads";
import { ErpCustomerFields, checkErpCustomer, trimErpCustomer, type ErpCustomer } from "../ErpCustomerFields";
import Ic from "../icons.jsx";

/**
 * Converting a won lead into a customer, which goes to the ERP at once. The dialog converts only with what the ERP
 * needs to open the customer, entered as for a customer entered by hand (see {@link ErpCustomerFields}), and starts
 * from the lead's country and address.
 */
export function ConvertLeadDialog({
  leadName,
  leadCountry,
  leadAddress,
  groups,
  busy,
  onClose,
  onConvert,
}: {
  leadName: string;
  leadCountry: string | null;
  /** The lead's address, which the dialog starts from. */
  leadAddress?: { addressLine1: string; postalCode: string; city: string };
  /** The ERP's customer groups in the order of its tree, each labelled with its path there. */
  groups: ErpGroupOption[];
  busy: boolean;
  onClose: () => void;
  onConvert: (body: ConvertRequest) => void;
}) {
  const { t } = useTranslation();
  const [erp, setErp] = useState<ErpCustomer>({
    vatNumber: "",
    registrationNumber: "",
    country: leadCountry ?? "RS",
    addressLine1: leadAddress?.addressLine1 ?? "",
    postalCode: leadAddress?.postalCode ?? "",
    city: leadAddress?.city ?? "",
    erpGroup: "",
  });
  const readyForErp = checkErpCustomer(erp).ready;

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={t("leads.convertDialog.title", "Convert to customer")}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={panelHead}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>
            {t("leads.convertDialog.title", "Convert to customer")}: {leadName}
          </h2>
          <button type="button" className="btn sm" onClick={onClose} aria-label={t("action.cancel", "Cancel")}>
            <Ic.x />
          </button>
        </div>

        <p style={{ margin: "0 0 14px", color: "var(--ink-2)", fontSize: 13.5 }}>
          {t("erpCustomer.intro", "The customer goes to the ERP at once. Required: tax number, registration number, country, customer kind and address.")}
        </p>

        <ErpCustomerFields value={erp} onChange={setErp} groups={groups} autoFocus />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="btn primary" onClick={() => onConvert(trimErpCustomer(erp))} disabled={busy || !readyForErp}>
            <Ic.handshake /> {t("leads.convertDialog.toErp", "Convert and send to the ERP")}
          </button>
        </div>
      </div>
    </div>
  );
}

// Flex, not grid: a grid track grows to the panel's 560 px, so maxWidth 100% would not hold it on a phone.
const overlay: CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 60 };
const panel: CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)", boxShadow: "var(--shadow-2)", padding: 22, width: 560, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" };
const panelHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 };
